import { useEffect, useState } from "react";
import { Crosshair, LocateFixed, Minus, Plus, SlidersHorizontal } from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import type { MapHandle } from "@/map/MapCanvas.tsx";
import { useMediaQuery } from "@/platform/use-media-query.ts";
import { SF_CENTER } from "@/search/search.state.ts";
import { cn } from "@/design-system/cn.ts";

/**
 * The map's own controls, in React rather than MapLibre's built-ins.
 *
 * MapLibre's NavigationControl/GeolocateControl label themselves with `title`,
 * which renders the browser's native tooltip — unstyled, on its own timing, and
 * invisible on touch. Rebuilding them here is what lets every control in the app
 * share one hint component.
 *
 * Basemap credit (OpenFreeMap · OpenMapTiles · © OpenStreetMap contributors)
 * lives in the README rather than on the map itself.
 */

export function MapControls({
  handle,
  ready,
  onOpenFilters,
  className,
  style,
}: {
  handle: React.RefObject<MapHandle | null>;
  /** Re-render trigger: the map instance is behind a ref. */
  ready: boolean;
  /**
   * Opens the filter drawer. Sits at the top of this stack rather than in the
   * top bar so that everything you touch on a phone is inside one thumb's reach
   * of the bottom-right corner. Omitted on desktop, where the console has its
   * own always-visible entry point and a pointer has no reach problem.
   */
  onOpenFilters?: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [locating, setLocating] = useState(false);
  const [zoom, setZoom] = useState<number | null>(null);

  // A landscape phone is ~390px tall; the full stack is ~223px and would crowd
  // or clip against the top bar and the tool dock. Below that, drop to the two
  // controls you can't do without and shrink the hit targets a little.
  const short = useMediaQuery("(max-height: 620px)");
  const btn = short ? "size-9" : "size-10";

  // Track zoom so the +/- buttons can disable at the limits instead of
  // silently doing nothing.
  useEffect(() => {
    const map = handle.current?.map;
    if (!map) return;
    const sync = () => setZoom(map.getZoom());
    sync();
    map.on("zoom", sync);
    return () => {
      map.off("zoom", sync);
    };
  }, [handle, ready]);

  const map = handle.current?.map ?? null;
  const atMax = zoom !== null && map !== null && zoom >= map.getMaxZoom() - 0.01;
  const atMin = zoom !== null && map !== null && zoom <= map.getMinZoom() + 0.01;

  const locate = () => {
    if (!navigator.geolocation || !map) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        map.easeTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: Math.max(map.getZoom(), 14),
          duration: 900,
          essential: true,
        });
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  return (
    <div
      className={cn("pointer-events-auto flex flex-col items-end gap-2", className)}
      style={style}
    >
      {onOpenFilters && (
        <Hint label="Filters" detail="Freshness, layout and area." side="left">
          <button
            type="button"
            onClick={onOpenFilters}
            aria-label="Open controls"
            className={cn(
              "console grid place-items-center rounded-lg text-chalk transition hover:text-chalk active:scale-95",
              btn,
            )}
          >
            <SlidersHorizontal className="size-4" />
          </button>
        </Hint>
      )}

      <Hint label="Find my location" side="left">
        <button
          type="button"
          onClick={locate}
          aria-label="Find my location"
          className={cn(
            "console grid place-items-center rounded-lg text-chalk-dim transition hover:text-chalk active:scale-95",
            btn,
          )}
        >
          <LocateFixed className={cn("size-4", locating && "animate-pulse text-signal-ink")} />
        </button>
      </Hint>

      {!short && (
        <Hint label="Recentre on San Francisco" side="left">
          <button
            type="button"
            onClick={() => handle.current?.flyTo(SF_CENTER, 12.2)}
            aria-label="Recentre on San Francisco"
            className="console grid size-10 place-items-center rounded-lg text-chalk-dim transition hover:text-chalk active:scale-95"
          >
            <Crosshair className="size-4" />
          </button>
        </Hint>
      )}

      {/* One slab for the zoom pair: they're a single control, not two. */}
      <div className="console overflow-hidden rounded-lg">
        <Hint label="Zoom in" side="left">
          <button
            type="button"
            onClick={() => map?.zoomIn({ duration: 260 })}
            disabled={atMax}
            aria-label="Zoom in"
            className={cn(
              btn,
              "grid place-items-center text-chalk-dim transition hover:bg-ink-600 hover:text-chalk active:scale-95 disabled:pointer-events-none disabled:opacity-30",
            )}
          >
            <Plus className="size-4" />
          </button>
        </Hint>
        <div className="h-px bg-rule" />
        <Hint label="Zoom out" side="left">
          <button
            type="button"
            onClick={() => map?.zoomOut({ duration: 260 })}
            disabled={atMin}
            aria-label="Zoom out"
            className={cn(
              btn,
              "grid place-items-center text-chalk-dim transition hover:bg-ink-600 hover:text-chalk active:scale-95 disabled:pointer-events-none disabled:opacity-30",
            )}
          >
            <Minus className="size-4" />
          </button>
        </Hint>
      </div>
    </div>
  );
}
